import { useCallback } from 'react';

import { useMutation } from '@tanstack/react-query';

import { displayToastError } from '@/helpers';
import { IntegrationServices } from '@/modules/common/types/integrations';

import { generateOAuthUrl, OAuthProvider } from '../api/oauth-integrations.api';
import { saveOAuthState } from '../utils/oauth-state';

/**
 * Maps IntegrationServices to OAuthProvider enum
 */
const mapIntegrationToOAuthProvider = (service: IntegrationServices): OAuthProvider | null => {
  switch (service) {
    case IntegrationServices.JIRA:
      return OAuthProvider.JIRA;
    case IntegrationServices.QUICKBOOKS:
      return OAuthProvider.QUICK_BOOKS;
    default:
      return null;
  }
};

/**
 * Hook to initiate OAuth flow for integrations
 * Generates OAuth URL, saves state, and opens the provider in a new tab
 */
export const useInitiateOAuthFlow = () => {
  const mutation = useMutation({
    mutationFn: async ({
      service,
      returnUrl,
    }: {
      service: IntegrationServices;
      returnUrl: string;
      oauthTab: Window | null;
    }) => {
      const provider = mapIntegrationToOAuthProvider(service);

      if (!provider) {
        throw new Error(`OAuth not supported for ${service}`);
      }

      const oauthUrl = await generateOAuthUrl(provider);

      // Save state before redirecting
      saveOAuthState(service, returnUrl);

      return oauthUrl;
    },
    onSuccess: (oauthUrl, { oauthTab }) => {
      if (!oauthTab) {
        window.location.href = oauthUrl;
        return;
      }

      oauthTab.location.href = oauthUrl;
    },
    onError: (error: Error, { oauthTab }) => {
      oauthTab?.close();
      displayToastError(error.message);
    },
  });

  const initiateOAuth = useCallback(
    (service: IntegrationServices) => {
      const returnUrl = window.location.pathname + window.location.search;
      const oauthTab = window.open('', '_blank');
      if (oauthTab) oauthTab.opener = null;

      mutation.mutate({ service, returnUrl, oauthTab });
    },
    [mutation]
  );

  return {
    initiateOAuth,
    isInitiating: mutation.isPending,
  };
};
